// Contenido completo para src/components/modules/UsuariosPermisos.js

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, getDocs } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import UserFormModal from '../ui/UserFormModal';

function UsuariosPermisosModule({ showModal, db, auth, permissions }) {
    const canManage = permissions?.USUARIOS_PERMISOS?.manage;
    const [users, setUsers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [currentUserToEdit, setCurrentUserToEdit] = useState(null);

    useEffect(() => {
        if(!db) return;
        const locationsCollectionRef = collection(db, 'inventory_locations');
        const getLocations = async () => {
             const querySnapshot = await getDocs(locationsCollectionRef);
             setLocations(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
        getLocations();
    }, [db]);

    useEffect(() => {
        setIsLoading(true);
        const usersCollectionRef = collection(db, 'users');
        const q = query(usersCollectionRef, orderBy('name'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const usersList = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
            setUsers(usersList);
            setIsLoading(false);
        }, (error) => {
            console.error(error);
            showModal("Error", "No se pudieron cargar los usuarios.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, showModal]);

    const handleSaveUser = async (userData) => {
        setIsLoading(true);
        try {
            if (currentUserToEdit) {
                // --- LÓGICA DE ACTUALIZACIÓN (Esta no cambia) ---
                const rolesRequiringLocation = ['bodega', 'produccion'];
                const userDocRef = doc(db, 'users', currentUserToEdit.uid);
                
                const dataToUpdate = {
                    name: userData.name,
                    role: userData.role,
                    location: rolesRequiringLocation.includes(userData.role) ? userData.location : '',
                    permissions: userData.permissions,
                    accountStatus: userData.accountStatus, 
                };

                await updateDoc(userDocRef, dataToUpdate);
                showModal("Éxito", "Usuario actualizado correctamente.");

            } else {
                // --- LÓGICA DE CREACIÓN (Llamando a la Cloud Function) ---
                if (!userData.email || !userData.password) {
                    showModal("Error", "Email y contraseña son requeridos para nuevos usuarios.");
                    setIsLoading(false);
                    return;
                }

                // Obtener una referencia a la Cloud Function que desplegamos
                const functions = getFunctions();
                const createUserAccount = httpsCallable(functions, 'createUserAccount');
                
                // Llamar a la función con los datos del formulario
                await createUserAccount({
                    email: userData.email,
                    password: userData.password,
                    name: userData.name,
                    role: userData.role,
                    location: userData.location,
                    permissions: userData.permissions,
                });
                
                showModal("Éxito", "Usuario creado correctamente.");
            }
            setIsUserModalOpen(false);
            setCurrentUserToEdit(null);
        } catch (error) {
            console.error("Error al guardar usuario:", error);
            // El mensaje de error de la Cloud Function es más claro.
            const friendlyMessage = error.message || "No se pudo guardar el usuario.";
            showModal("Error", friendlyMessage);
        }
        setIsLoading(false);
    };

    const handleResetPasswordAdmin = async (email) => {
        setIsLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            showModal("Correo Enviado", `Se ha enviado un correo de restablecimiento a ${email}.`);
        } catch(error) {
            showModal("Error", "No se pudo enviar el correo.");
        }
        setIsLoading(false);
    };

    if (isLoading) {
        return <div className="text-center py-10"><p>Cargando usuarios...</p></div>;
    }

    if (!permissions?.USUARIOS_PERMISOS?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }

    const getLocationName = (locationId) => {
        if (!locationId) return 'N/A';
        const location = locations.find(l => l.id === locationId);
        return location ? location.name : 'Desconocida';
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Gestionar Usuarios y Permisos</h2>
                {canManage && (
                    <button onClick={() => { setCurrentUserToEdit(null); setIsUserModalOpen(true); }} className="btn-primary">+ Nuevo Usuario</button>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Nombre</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Correo</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Rol</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Estado</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Ubicación</th>
                            <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.uid} className="border-b hover:bg-sky-50">
                                <td className="p-3">{user.name}</td>
                                <td className="p-3">{user.email}</td>
                                <td className="p-3 capitalize">{user.role}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold
                                        ${user.accountStatus === 'suspendido' 
                                            ? 'bg-red-100 text-red-800' 
                                            : 'bg-green-100 text-green-800'
                                        }`}
                                    >
                                        {user.accountStatus === 'suspendido' ? 'Suspendido' : 'Activo'}
                                    </span>
                                </td>
                                <td className="p-3">{getLocationName(user.location)}</td>
                                <td className="p-3 text-center">
                                    {canManage && (
                                        <div className="flex justify-center items-center space-x-2">
                                            <button onClick={() => { setCurrentUserToEdit(user); setIsUserModalOpen(true); }} className="text-sky-600 hover:text-sky-800 p-1" title="Editar"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg></button>
                                            <button onClick={() => handleResetPasswordAdmin(user.email)} className="text-yellow-600 hover:text-yellow-800 p-1" title="Resetear Contraseña"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.414V7z" clipRule="evenodd" /></svg></button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <UserFormModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                onSave={handleSaveUser}
                initialData={currentUserToEdit}
                isLoading={isLoading}
                showAppModal={showModal}
                locations={locations}
            />
        </div>
    );
}

export default UsuariosPermisosModule;